import type { ReactNode } from 'react';
import type { StorefrontCatalogItem, StorefrontStore } from '@storeo/api-client';
import { isSectionVisible, logoSrc, orderedSectionKeys } from './sections';

/** Kit compartilhado dos 6 templates Lumina do redesign (docs/prototype/storeo_studio_template_*).
 *  Cada template é cinematográfico e distinto, mas divide cabeçalho, grade de produtos,
 *  CTA de compra, depoimento e rodapé. Usa a marca real da loja (nome, tagline, logo, 2 cores
 *  via `--sf-primary`/`--sf-accent` injetadas pelo `StoreTheme`/preview) e os produtos reais.
 *
 *  Pacote compartilhado entre `apps/storefront` (Next, real) e `apps/painel` (Vite, preview do
 *  Studio) — por isso não importa nada de `next/*`: links são `<a>` simples, e URLs de arquivo
 *  (`logoSrc`) recebem `apiBase` explícito em vez de ler `process.env` (cada app resolve o seu). */

export function money(v: number): string {
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Raiz de qualquer template: escopo `.lumina` (Tailwind) + fundo `--sf-primary`. */
export function TplRoot({ font = 'sans', children }: { font?: 'sans' | 'serif'; children: ReactNode }) {
  return (
    <div
      className="lumina min-h-screen bg-[var(--sf-primary,#0a0e17)] text-white antialiased"
      style={font === 'serif' ? { fontFamily: "'Playfair Display', Georgia, serif" } : { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      {children}
    </div>
  );
}

export function TplHeader({ store, apiBase, suffix, links }: { store: StorefrontStore; apiBase: string; suffix?: string; links?: string[] }) {
  const logo = logoSrc(store.logoUrl, apiBase);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/40 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <a href="/" className="flex items-center gap-2.5 min-w-0">
          {logo ? (
            <img src={logo} alt="" className="h-7 w-auto object-contain" />
          ) : (
            <span className="h-7 w-7 rounded-lg bg-[var(--sf-accent,#4edea3)] shrink-0" />
          )}
          <span className="font-bold tracking-tight truncate">
            {store.name}
            {suffix ? <span className="text-[var(--sf-accent,#4edea3)]">{suffix}</span> : null}
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm text-white/60">
          {(links ?? ['Coleção', 'Sobre', 'Contato']).map((l) => (
            <span key={l} className="hover:text-white transition-colors cursor-default">
              {l}
            </span>
          ))}
        </nav>
      </div>
    </header>
  );
}

export function BuyLink({
  href,
  children,
  className = '',
  block,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  block?: boolean;
}) {
  return (
    <a
      href={href}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--sf-accent,#4edea3)] text-black font-bold px-6 py-3.5 shadow-[0_0_30px_-4px_var(--sf-accent,#4edea3)] hover:brightness-110 transition-all ${
        block ? 'w-full' : ''
      } ${className}`}
    >
      {children}
    </a>
  );
}

export function ProductGrid({
  products,
  cols = 3,
  cardCta = 'Ver produto',
}: {
  products: StorefrontCatalogItem[];
  cols?: 2 | 3 | 4;
  cardCta?: string;
}) {
  if (products.length === 0) {
    return <p className="text-center text-white/40 py-16">Ainda não há produtos publicados nesta loja.</p>;
  }
  const grid = cols === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : cols === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';
  return (
    <div className={`grid grid-cols-1 gap-5 ${grid}`}>
      {products.map((p) => (
        <a
          key={p.slug}
          href={`/p/${p.slug}`}
          className="group rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden hover:border-[var(--sf-accent,#4edea3)]/50 hover:bg-white/[0.06] transition-all flex flex-col"
        >
          <div className="aspect-[4/3] bg-gradient-to-br from-white/[0.08] to-transparent flex items-center justify-center">
            <span className="text-white/20 text-4xl font-black">{p.name.charAt(0)}</span>
          </div>
          <div className="p-4 flex flex-col gap-1.5 flex-1">
            <span className="text-[11px] uppercase tracking-widest text-[var(--sf-accent,#4edea3)] font-bold">
              {p.type === 'subscription' ? 'Assinatura' : p.deliveryChannel === 'physical' ? 'Produto físico' : 'Infoproduto'}
            </span>
            <h3 className="font-bold leading-tight">{p.name}</h3>
            {p.description ? <p className="text-sm text-white/50 line-clamp-2">{p.description}</p> : null}
            <div className="mt-auto pt-2 flex items-center justify-between">
              <span className="font-black text-lg">
                {money(p.amount)}
                {p.type === 'subscription' ? <span className="text-xs font-medium text-white/50">/mês</span> : null}
              </span>
              <span className="text-sm text-[var(--sf-accent,#4edea3)] font-semibold group-hover:translate-x-0.5 transition-transform">
                {cardCta} →
              </span>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

export function Quote({ store }: { store: StorefrontStore }) {
  if (!isSectionVisible(store, 'quote') || !store.testimonialQuote) return null;
  return (
    <blockquote className="max-w-2xl mx-auto text-center py-12">
      <p className="text-xl md:text-2xl font-medium italic text-white/90">“{store.testimonialQuote}”</p>
      {store.testimonialAuthor ? <cite className="block mt-3 text-sm text-white/50 not-italic">— {store.testimonialAuthor}</cite> : null}
    </blockquote>
  );
}

export function TplFooter({ store }: { store: StorefrontStore }) {
  return (
    <footer className="border-t border-white/10 mt-16">
      <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
        <span>
          © {new Date().getFullYear()} {store.name}
        </span>
        <span className="flex items-center gap-3">
          <span>Pagamento processado por Storeo</span>
          <span>·</span>
          <span>Conexão segura (SSL)</span>
        </span>
      </div>
    </footer>
  );
}

/** feature + resto, respeitando `store.sections`. */
export function splitProducts(store: StorefrontStore, products: StorefrontCatalogItem[]) {
  const feature = products.find((p) => p.featured) ?? products[0] ?? null;
  const showFeat = isSectionVisible(store, 'feat') && !!feature;
  const showGrid = isSectionVisible(store, 'grid');
  const grid = showFeat && feature ? products.filter((p) => p !== feature) : products;
  const featFirstInOrder = orderedSectionKeys(store).indexOf('feat') < orderedSectionKeys(store).indexOf('grid');
  return { feature: showFeat ? feature : null, grid: showGrid ? grid : [], featFirstInOrder };
}

export const DELIVERY_NOTE: Record<string, string> = {
  digital_file: 'Download liberado na hora do pagamento',
  digital_link: 'Acesso enviado na hora por e-mail',
  video: 'Assista na área do cliente, sem download',
  telegram: 'Entrada no grupo do Telegram automática',
  physical: 'Enviado após a confirmação do pagamento',
};

/** Página de produto compartilhada — cada template passa `font`, `suffix`, `kicker`, `ctaLabel`.
 *  `checkoutSlot` é onde o app real planta o formulário de checkout de verdade (funcional, com
 *  `next/navigation` etc — por isso não mora aqui); sem ele, o preview mostra um cartão estático.
 *  `relatedProducts` (opcional) liga a seção "Mais da loja" com o resto do catálogo. */
export function ProductPage({
  data,
  apiBase,
  font = 'sans',
  suffix,
  kicker,
  ctaLabel = 'Comprar agora',
  relatedProducts,
  checkoutSlot,
}: {
  data: { store: StorefrontStore; product: StorefrontCatalogItem };
  apiBase: string;
  font?: 'sans' | 'serif';
  suffix?: string;
  kicker?: string;
  ctaLabel?: string;
  relatedProducts?: StorefrontCatalogItem[];
  checkoutSlot?: ReactNode;
}) {
  const { store, product: p } = data;
  const related = (relatedProducts ?? []).filter((r) => r.slug !== p.slug).slice(0, 3);
  return (
    <TplRoot font={font}>
      <TplHeader store={store} apiBase={apiBase} suffix={suffix} />
      <main className="max-w-5xl mx-auto px-4 py-10">
        <a href="/" className="text-sm text-white/50 hover:text-white transition-colors">
          ← Voltar para a loja
        </a>

        <article className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="rounded-2xl aspect-square bg-gradient-to-br from-white/[0.1] via-[var(--sf-accent,#4edea3)]/10 to-transparent border border-white/10 flex items-center justify-center">
            <span className="text-white/15 text-8xl font-black">{p.name.charAt(0)}</span>
          </div>
          <div className="flex flex-col gap-4">
            {kicker ? (
              <span className="text-[11px] uppercase tracking-widest text-[var(--sf-accent,#4edea3)] font-bold">{kicker}</span>
            ) : (
              <span className="text-sm text-white/50">{store.name}</span>
            )}
            <h1 className="text-3xl md:text-4xl font-black leading-tight tracking-tight">{p.name}</h1>
            {p.description ? <p className="text-white/70 leading-relaxed">{p.description}</p> : null}

            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 flex flex-col gap-3 mt-2">
              <div className="text-3xl font-black">
                {money(p.amount)}
                {p.type === 'subscription' ? <span className="text-base font-medium text-white/50">/mês</span> : null}
              </div>
              <BuyLink href={checkoutSlot ? '#checkout' : `/p/${p.slug}/checkout`} block className="text-base">
                {ctaLabel}
              </BuyLink>
              <p className="text-xs text-white/50 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--sf-accent,#4edea3)]" />
                {DELIVERY_NOTE[p.deliveryChannel] ?? 'Entrega automática após o pagamento'}
              </p>
              <p className="text-xs text-white/40">Pagamento seguro · PIX e cartão · processado por Storeo</p>
            </div>
          </div>
        </article>

        {related.length > 0 ? (
          <section className="mt-16">
            <h2 className="text-xl font-black tracking-tight mb-5">Mais da loja</h2>
            <ProductGrid products={related} cols={3} />
          </section>
        ) : null}

        <Quote store={store} />

        {checkoutSlot ? (
          <section id="checkout" className="mt-4 scroll-mt-20">
            {checkoutSlot}
          </section>
        ) : null}
      </main>
      <TplFooter store={store} />
    </TplRoot>
  );
}
